import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoMark } from "./components/Logo";
import { MacaKit, PillCTA, Highlight, Scribble, GlassArt, COBALT } from "./components/Maca";
import {
  VSLPlaceholder,
  StickyCTABar,
  FeaturedInStrip,
  GuaranteeBadge,
  ProblemSection,
  MeetErnesto,
  ForbesStrip,
  VerticalOffers,
  FAQSection,
  ScarcityBar,
  MidScrollCTA,
  RecentActivity,
  PriceIncreaseBanner,
  BonusStack,
  CertificateMockup,
  PaymentMethods,
  PersonalNote,
} from "./components/ConversionKit";
import { MediaFeatures, OfferStack, BeforeAfter, GuaranteeSection, EbookLibrary } from "./components/TrustKit";

const GOLD = "#2F2BEB";
const BLUE = "#2F2BEB";
const LIME = "#D9F154";
const CREAM = "#141414";
const BG = "#FAF7EF";
const BG_ALT = "#FFFFFF";
const BG_ELEV = "#FFFFFF";

// Single source of truth for the "next group closes" date — the next 28th of the month.
const MONTHS_ES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
const nextCycleDate = (dayOfMonth = 28) => {
  const now = new Date();
  let target = new Date(now.getFullYear(), now.getMonth(), dayOfMonth, 23, 59, 59);
  if (target.getTime() <= now.getTime()) target = new Date(now.getFullYear(), now.getMonth() + 1, dayOfMonth, 23, 59, 59);
  return target;
};
const fmtDateEs = (d) => `${d.getDate()} de ${MONTHS_ES[d.getMonth()]}, ${d.getFullYear()}`;
const calcLeft = (target) => {
  const diff = Math.max(0, target.getTime() - Date.now());
  return {
    d: Math.floor(diff / 86400000),
    h: Math.floor((diff % 86400000) / 3600000),
    m: Math.floor((diff % 3600000) / 60000),
    s: Math.floor((diff % 60000) / 1000),
  };
};

// Distinct line-art icons per audience.
const TrackIcon = ({ kind, size = 44, accent = GOLD }) => {
  const sw = 1.3;
  switch (kind) {
    case "startups":
      // upward arrow — velocity / early stage
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={accent} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
          <path d="M 8 52 L 22 38 L 32 44 L 56 20" />
          <path d="M 44 20 L 56 20 L 56 32" />
          <line x1="8" y1="58" x2="56" y2="58" />
        </svg>
      );
    case "smbs":
      // storefront
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={accent} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
          <path d="M 8 22 L 12 12 L 52 12 L 56 22" />
          <line x1="6" y1="22" x2="58" y2="22" />
          <path d="M 10 22 L 10 54 L 54 54 L 54 22" />
          <path d="M 25 54 L 25 38 L 39 38 L 39 54" />
          <line x1="14" y1="28" x2="22" y2="28" />
        </svg>
      );
    case "lenders":
      // stacked bars — credit platforms
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={accent} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
          <rect x="10" y="32" width="10" height="22" />
          <rect x="27" y="18" width="10" height="36" />
          <rect x="44" y="26" width="10" height="28" />
          <line x1="8" y1="54" x2="56" y2="54" />
          <path d="M 8 12 L 22 22 L 38 14 L 54 20" />
        </svg>
      );
    case "institutions":
      // portico columns
      return (
        <svg width={size} height={size} viewBox="0 0 64 64" fill="none" stroke={accent} strokeWidth={sw} strokeLinecap="round" strokeLinejoin="round">
          <path d="M 6 16 L 32 6 L 58 16" />
          <line x1="6" y1="20" x2="58" y2="20" />
          <line x1="14" y1="24" x2="14" y2="48" />
          <line x1="26" y1="24" x2="26" y2="48" />
          <line x1="38" y1="24" x2="38" y2="48" />
          <line x1="50" y1="24" x2="50" y2="48" />
          <line x1="6" y1="52" x2="58" y2="52" />
          <line x1="4" y1="56" x2="60" y2="56" />
        </svg>
      );
    default:
      return null;
  }
};

const PROOF_LOGOS = ["ERGO Capital", "Sivo", "Trafalgar", "Grupo Fagro", "Kapital México", "BAASA"];

export default function Hub() {
  const closeDate = nextCycleDate(28);
  const closeLabel = fmtDateEs(closeDate);
  const [timeLeft, setTimeLeft] = useState(() => calcLeft(closeDate));

  useEffect(() => {
    document.title = "The Debt Guy · Estructura y levanta deuda como un insider";
  }, []);

  useEffect(() => {
    const timer = setInterval(() => setTimeLeft(calcLeft(nextCycleDate(28))), 1000);
    return () => clearInterval(timer);
  }, []);

  const pad = (n) => String(n).padStart(2, "0");

  return (
    <div style={{ background: BG, color: CREAM, minHeight: "100vh", overflowX: "hidden", position: "relative" }} data-testid="hub-root" id="top">
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Archivo:ital,wght@0,100..900;1,100..900&family=Archivo+Black&display=swap');
        * { box-sizing: border-box; margin: 0; padding: 0; }
        html, body { overflow-x: hidden; width: 100%; background: ${BG}; scroll-behavior: smooth; scroll-padding-top: 90px; }
        body { font-family: 'Archivo', sans-serif; color: ${CREAM}; -webkit-font-smoothing: antialiased; }
        h1, h2, h3, .hero-title, .section-title, .hub-hero-title, .fraunces-hero, .track-title, .track-subtitle, .pro-price { font-family: 'Archivo', sans-serif !important; font-style: normal !important; letter-spacing: -0.03em; }
        h1, h2, .hero-title, .section-title, .hub-hero-title, .fraunces-hero, .track-subtitle, .pro-price { font-weight: 900 !important; }
        .track-title { font-weight: 800 !important; }
        h1 .serif, h2 .serif, .hero-title .serif, .section-title .serif, .hub-hero-title .serif { font-weight: 900 !important; font-style: normal !important; }
        .tag { font-weight: 700 !important; }
        ::selection { background: ${LIME}; color: #141414; }

        .hub-grain::before {
          content: ''; position: fixed; inset: 0;
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.85' /%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.02'/%3E%3C/svg%3E");
          pointer-events: none; z-index: 1; mix-blend-mode: multiply;
        }
        .serif { font-family: 'Archivo', sans-serif; font-weight: 800; letter-spacing: -0.02em; }
        .mono { font-family: 'Archivo', sans-serif; }
        .tag { font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.3em; text-transform: uppercase; color: ${BLUE}; font-weight: 700; }

        .hub-hero-title {
          font-family: 'Archivo', sans-serif; font-weight: 900; letter-spacing: -0.02em;
          font-size: clamp(48px, 7vw, 108px); line-height: 0.94; letter-spacing: -0.045em;
        }

        /* Bloomberg-terminal ticker */
        .tcg-ticker {
          background: ${BG_ALT};
          border-bottom: 1px solid ${GOLD}33;
          overflow: hidden; position: relative;
          padding: 8px 0; z-index: 20;
        }
        .tcg-ticker-track {
          display: inline-flex; gap: 42px;
          animation: tcg-ticker-scroll 48s linear infinite;
          padding-left: 100%;
          white-space: nowrap;
          will-change: transform;
        }
        .tcg-ticker-item {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Archivo', sans-serif; font-size: 10.5px;
          letter-spacing: 0.14em; text-transform: uppercase; color: ${CREAM}; opacity: 0.85;
        }
        .tcg-ticker-item .k { color: ${CREAM}; opacity: 0.55; }
        .tcg-ticker-item .v { color: ${CREAM}; font-weight: 500; }
        .tcg-ticker-item .up { color: ${GOLD}; font-weight: 600; }
        .tcg-ticker-item .down { color: #FF6B35; font-weight: 600; }
        .tcg-ticker-item .sep { color: ${GOLD}; opacity: 0.4; margin: 0 6px; }
        @keyframes tcg-ticker-scroll {
          0% { transform: translateX(0); }
          100% { transform: translateX(-100%); }
        }

        /* Editorial serif override for hero big words */
        .fraunces-hero {
          font-family: 'Archivo', sans-serif;
          font-variation-settings: "SOFT" 100, "opsz" 144;
          font-style: normal; font-weight: 900;
        }

        .track-card {
          position: relative; background: ${BG_ALT};
          border: 2px solid #141414; border-radius: 18px; box-shadow: 5px 5px 0 #141414; padding: 36px 32px;
          text-decoration: none; color: ${CREAM};
          display: flex; flex-direction: column;
          transition: background 0.35s ease, border-color 0.35s ease, transform 0.35s ease;
          overflow: hidden;
          min-height: 480px;
        }
        .track-card:hover { transform: translate(3px,3px); box-shadow: 2px 2px 0 #141414; }
        .track-card::after {
          content: ""; position: absolute; left: 0; right: 0; bottom: 0; height: 2px;
          background: var(--accent, ${GOLD}); transform: scaleX(0); transform-origin: left center;
          transition: transform 0.45s cubic-bezier(0.16,1,0.3,1);
        }
        .track-card:hover::after { transform: scaleX(1); }

        .track-num { font-family: 'Archivo', sans-serif; font-size: 10px; letter-spacing: 0.3em; text-transform: uppercase; color: var(--accent, ${GOLD}); opacity: 0.9; }
        .track-status {
          display: inline-flex; align-items: center; gap: 8px;
          font-family: 'Archivo', sans-serif; font-size: 9.5px;
          letter-spacing: 0.2em; text-transform: uppercase;
          color: inherit; opacity: 0.9;
          background: var(--soft, rgba(15,56,44,0.06)); border-radius: 6px;
          padding: 6px 10px; margin-top: 16px; width: fit-content;
        }
        .track-dot { width: 6px; height: 6px; border-radius: 50%; }
        .track-title { font-family: 'Archivo', sans-serif; font-weight: 800; font-size: 24px; line-height: 1.05; letter-spacing: -0.01em; margin-top: 24px; text-transform: uppercase; }
        .track-subtitle { font-family: 'Archivo', sans-serif; font-style: normal; font-weight: 900; font-size: 28px; line-height: 1.05; letter-spacing: -0.02em; color: var(--accent, ${GOLD}); }
        .track-kicker { font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: inherit; opacity: 0.65; margin-top: 18px; }
        .track-line { font-size: 14.5px; line-height: 1.55; margin-top: 12px; color: inherit; opacity: 0.85; }
        .track-stats { margin-top: auto; display: flex; flex-direction: column; gap: 10px; padding-top: 26px; border-top: 1px solid var(--soft, rgba(15,56,44,0.08)); }
        .track-stat { display: flex; align-items: baseline; gap: 12px; font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.08em; color: inherit; opacity: 0.85; }
        .track-stat::before { content: ""; display: inline-block; width: 14px; height: 1px; background: var(--accent, ${GOLD}); flex-shrink: 0; }
        .track-cta { margin-top: 26px; display: inline-flex; align-items: center; gap: 10px; font-family: 'Archivo', sans-serif; font-size: 10.5px; letter-spacing: 0.22em; text-transform: uppercase; color: var(--accent, ${GOLD}); transition: gap 0.3s ease; }
        .track-card:hover .track-cta { gap: 16px; }

        .icon-wrap { width: 60px; height: 60px; display: flex; align-items: center; justify-content: center; border: 2px solid #141414; border-radius: 50%; background: #FFFFFF; }

        @keyframes fadeUp { from { opacity: 0; transform: translateY(18px); } to { opacity: 1; transform: translateY(0); } }
        .fade-up { animation: fadeUp 0.9s cubic-bezier(0.16,1,0.3,1) both; }

        .grid-tracks { display: grid; grid-template-columns: repeat(4, 1fr); gap: 16px; }
        @media (max-width: 1400px) { .grid-tracks { grid-template-columns: repeat(2, 1fr); } .track-card { min-height: 440px; } }
        @media (max-width: 700px) { .grid-tracks { grid-template-columns: 1fr; } .track-card { min-height: unset; } }

        .newsletter-form { display: flex; gap: 10px; margin-top: 22px; max-width: 480px; flex-wrap: wrap; }
        .newsletter-input {
          flex: 1; min-width: 200px;
          background: #FFFFFF; border: 2px solid #141414; border-radius: 10px;
          color: ${CREAM}; font-family: 'Archivo', sans-serif; font-size: 14px;
          padding: 15px 16px; outline: none; border-radius: 10px;
        }
        .newsletter-input:focus { border-color: ${GOLD}; }

        .btn-primary {
          background: ${LIME}; color: #141414; padding: 15px 28px;
          font-family: 'Archivo', sans-serif; font-size: 11px; font-weight: 500;
          letter-spacing: 0.06em; text-transform: uppercase; border: 2px solid #141414; border-radius: 14px; cursor: pointer; box-shadow: 4px 4px 0 #141414; font-weight: 800;
          transition: all 0.3s; display: inline-block; text-decoration: none; text-align: center; white-space: nowrap;
        }
        .btn-primary:hover { transform: translate(2px,2px); box-shadow: 2px 2px 0 #141414; }

        .countdown-item {
          display: inline-flex; flex-direction: column; align-items: center;
          padding: 8px 14px; border: 1px solid rgba(15,56,44,0.12);
          background: ${BG_ALT};
        }
        .countdown-item .n { font-family: 'Archivo', sans-serif; font-size: 22px; color: ${BLUE}; letter-spacing: -0.02em; line-height: 1; }
        .countdown-item .l { font-family: 'Archivo', sans-serif; font-size: 8.5px; letter-spacing: 0.24em; opacity: 0.55; margin-top: 4px; text-transform: uppercase; }

        .logo-strip {
          display: flex; flex-wrap: wrap; gap: 28px 40px; opacity: 0.65;
          font-family: 'Archivo', sans-serif; font-size: 11px;
          letter-spacing: 0.22em; text-transform: uppercase; color: ${CREAM};
        }

        .hub-nav-link { font-family: 'Archivo', sans-serif; font-size: 12px; font-weight: 700; letter-spacing: 0.01em; color: ${CREAM}; text-decoration: none; opacity: 0.72; transition: opacity 0.2s, color 0.2s; }
        .hub-nav-link:hover { opacity: 1; color: ${BLUE}; }
        @media (max-width: 1080px) { .hub-nav-menu { display: none !important; } }

        @media (max-width: 900px) {
          .hub-nav { padding: 14px 20px !important; flex-wrap: wrap !important; gap: 12px !important; }
          .hub-nav > div:last-child { gap: 14px !important; flex-wrap: wrap !important; }
          .hub-hero { padding: 90px 20px 40px !important; }
          .hub-body { padding: 0 20px 100px !important; }
          .hub-footer { padding: 40px 20px !important; }
          .persona-strip { flex-direction: column !important; text-align: center; }
        }
        @media (max-width: 620px) {
          .hub-nav > div:last-child a:nth-child(n+3) { display: none; }
        }
        @media (max-width: 900px) {
          .hero-grid { grid-template-columns: 1fr !important; }
          .hero-art { min-height: 300px !important; margin-top: 16px; }
          .trk-inner { grid-template-columns: 1fr !important; padding: 28px 24px !important; }
          .trk-art { order: -1; margin: 0 auto 8px; max-width: 220px; }
        }
      `}</style>

      <div className="hub-grain" />
      <MacaKit />

      {/* Promo bar with live countdown — infoproduct urgency signal */}
      <div
        style={{
          background: "#3B5EDF", color: "#FFFFFF", padding: "10px 20px", textAlign: "center",
          fontFamily: "'Archivo', sans-serif", fontSize: 10.5,
          letterSpacing: "0.16em", textTransform: "uppercase", fontWeight: 500,
          position: "relative", zIndex: 20,
        }}
        data-testid="hub-promo-bar"
      >
        The Debt Guy · El próximo grupo cierra en {pad(timeLeft.d)}d · {pad(timeLeft.h)}h · {pad(timeLeft.m)}m · {pad(timeLeft.s)}s
      </div>

      {/* Nav — one-page, no menu, only brand */}
      <nav
        className="hub-nav"
        style={{
          position: "sticky", top: 0, zIndex: 30,
          background: `${BG}dd`, backdropFilter: "blur(20px)",
          borderBottom: "1px solid rgba(15,56,44,0.06)",
          padding: "16px 40px",
          display: "flex", justifyContent: "space-between", alignItems: "center",
        }}
        data-testid="hub-nav"
      >
        <a href="#top" style={{ display: "flex", alignItems: "center", gap: 12, textDecoration: "none", color: CREAM }}>
          <LogoMark size={74} />
        </a>
        <div style={{ display: "flex", alignItems: "center", gap: 24 }}>
          <div className="hub-nav-menu" style={{ display: "flex", gap: 22, alignItems: "center" }}>
            <Link to="/programas" className="hub-nav-link" data-testid="hub-nav-programas">Programas</Link>
            <Link to="/intangibles" className="hub-nav-link" data-testid="hub-nav-intangibles">Intangibles</Link>
            <Link to="/toolkit" className="hub-nav-link" data-testid="hub-nav-toolkit">Toolkit</Link>
            <Link to="/certificacion" className="hub-nav-link" data-testid="hub-nav-cert">Certificación</Link>
            <Link to="/circle" className="hub-nav-link" data-testid="hub-nav-circle">The Circle</Link>
            <Link to="/ernesto" className="hub-nav-link" data-testid="hub-nav-ernesto">Ernesto</Link>
          </div>
          <a href="#pricing" className="btn-primary" style={{ padding: "12px 22px", fontSize: 10 }} data-testid="hub-nav-pricing">
            Ver precios
          </a>
        </div>
      </nav>

      {/* Hero */}
      <section
        className="hub-hero mk-fadeup"
        style={{ padding: "64px 40px 36px", maxWidth: 1400, margin: "0 auto", position: "relative", zIndex: 5 }}
        data-testid="hub-hero"
      >
        <div className="hero-grid" style={{ display: "grid", gridTemplateColumns: "1.12fr 0.88fr", gap: 40, alignItems: "center" }}>
          <div>
            <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
              <span className="mk-livedot" style={{ width: 9, height: 9, borderRadius: "50%", background: COBALT, flexShrink: 0 }} />
              <span className="tag" data-testid="hub-hero-tag" style={{ color: COBALT }}>USD 1.2B+ EN TERM SHEETS FIRMADOS · 16+ AÑOS · 9 PAÍSES</span>
              <span style={{ flex: 1, height: 1, background: "rgba(20,20,20,0.12)", minWidth: 20 }} />
            </div>

            <div data-testid="hub-hero-intro" style={{ marginTop: 26, fontFamily: "'Archivo', sans-serif", fontSize: "clamp(20px, 2.2vw, 28px)", fontWeight: 500, letterSpacing: "-0.02em" }}>
              Hola, soy <span style={{ color: COBALT, fontWeight: 800 }}>Ernesto</span>.
            </div>

            <h1 className="hub-hero-title" style={{ marginTop: 10, maxWidth: 720 }} data-testid="hub-hero-title">
              Estructura y levanta<br />
              <span style={{ color: COBALT }}>deuda <Highlight>como un insider.</Highlight></span>
            </h1>

            <p style={{ marginTop: 28, fontFamily: "'Archivo', sans-serif", fontSize: "clamp(16px, 1.4vw, 19px)", lineHeight: 1.55, maxWidth: 560 }} data-testid="hub-hero-sub">
              <strong style={{ fontWeight: 800 }}>Llevo más de 16 años estructurando y cerrando deuda para fintechs, PyMEs y fondos en LATAM.</strong>{" "}
              <span style={{ opacity: 0.6 }}>Aquí te enseño a levantar deuda sin regalar tu empresa — o lo hago por ti. Y cuando toca M&A o capital markets, también.</span>
            </p>

            <div style={{ marginTop: 30, display: "flex", gap: 10, flexWrap: "wrap" }} data-testid="hub-hero-countdown">
              <div className="countdown-item"><span className="n">{pad(timeLeft.d)}</span><span className="l">días</span></div>
              <div className="countdown-item"><span className="n">{pad(timeLeft.h)}</span><span className="l">horas</span></div>
              <div className="countdown-item"><span className="n">{pad(timeLeft.m)}</span><span className="l">min</span></div>
              <div className="countdown-item"><span className="n">{pad(timeLeft.s)}</span><span className="l">seg</span></div>
            </div>
            <div className="mono" style={{ marginTop: 10, fontSize: 10, letterSpacing: "0.24em", opacity: 0.5, textTransform: "uppercase" }}>para que cierren los lugares del próximo grupo</div>

            <div style={{ marginTop: 30, display: "flex", gap: 16, flexWrap: "wrap", alignItems: "center" }}>
              <PillCTA href="#pricing" variant="lime" bolt testid="hub-hero-cta-pricing" big>Ver programas y precios</PillCTA>
            </div>
          </div>

          {/* Illustration + hand annotations */}
          <div className="hero-art" style={{ position: "relative", display: "flex", justifyContent: "center", alignItems: "center", minHeight: 360 }}>
            <div style={{ position: "absolute", right: -26, top: -10, width: 190, height: 190, background: COBALT, borderRadius: "44% 56% 62% 38%", opacity: 0.92, zIndex: 0 }} />
            <div style={{ position: "absolute", left: -14, bottom: -24, width: 140, height: 140, background: COBALT, borderRadius: "50%", opacity: 0.12, zIndex: 0 }} />
            <div style={{ position: "relative", zIndex: 2 }}>
              <GlassArt accent={COBALT} glyph="chart" width={330} height={320} />
            </div>
            <Scribble arrow="down-left" size={19} style={{ position: "absolute", top: -8, right: 4, zIndex: 3 }}>Más de 16 años<br />estructurando<br />deals reales.</Scribble>
          </div>
        </div>

        {/* proof logos */}
        <div style={{ marginTop: 40, borderTop: "1px solid rgba(20,20,20,0.08)", paddingTop: 26 }}>
          <div className="mono" style={{ fontSize: 10, letterSpacing: "0.28em", color: COBALT, marginBottom: 18, textTransform: "uppercase" }}>Han confiado en mí</div>
          <div className="logo-strip" data-testid="hub-logos">
            {PROOF_LOGOS.map((l) => (<span key={l}>{l}</span>))}
          </div>
        </div>
      </section>

      {/* VSL — 90 seconds with Ernesto */}
      <section
        style={{ padding: "60px 40px 40px", maxWidth: 1120, margin: "0 auto", position: "relative", zIndex: 5 }}
        data-testid="hub-vsl-section"
      >
        <VSLPlaceholder
          tag="— MÍRALO · BIENVENIDA"
          title={<>Cómo cierro deuda institucional <em style={{ fontStyle: "normal", color: GOLD }}>sin</em> diluir a los fundadores.</>}
          sub="Dale play — un mensaje rápido de bienvenida."
          runtime="0:08"
          testid="hub-vsl"
        />
      </section>

      {/* Featured in press */}
      <FeaturedInStrip testid="hub-featured-in" />

      {/* Live scarcity bar — seats filling */}
      <ScarcityBar
        filled={8}
        total={12}
        label="Grupo 03 · Inscripciones abiertas"
        deadline={`Cierra el ${closeLabel}`}
        testid="hub-scarcity"
      />

      {/* 07 · PROBLEM — 3 pain points */}
      <ProblemSection testid="hub-problem" />

      {/* Mid-CTA · after Problem */}
      <MidScrollCTA
        kicker="— ¿YA VISTE EL PROBLEMA?"
        headline={<>Estructúralo bien <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>desde la primera vez.</span></>}
        subline="Elige tu programa y deja de pagar de más por capital que ni siquiera necesitas."
        ctaText="Ver programas y precios"
        ctaHref="#pricing"
        secondaryText="Descarga un ebook gratis →"
        secondaryHref="#biblioteca"
        note="Acceso inmediato tras el pago."
        testid="hub-mid-cta-1"
      />

      {/* 08 · MEET ERNESTO — portrait + story + pull quote */}
      <MeetErnesto testid="hub-meet-ernesto" />

      {/* 08·forbes · PRESS / FORBES video proof */}
      <ForbesStrip testid="hub-forbes" />

      {/* 08b · EN LOS MEDIOS · prensa real + credencial Sivo */}
      <MediaFeatures testid="hub-media" />

      {/* 08a · PERSONAL NOTE — cream warm section (Maca-style intimacy) */}
      <PersonalNote testid="hub-personal-note" />

      {/* 08b · BIBLIOTECA GRATIS · ebook según interés (lead magnet) */}
      <EbookLibrary testid="hub-ebooks" />

      {/* 11b · ANTES / DESPUÉS · prime antes del precio */}
      <BeforeAfter testid="hub-before-after" />

      {/* THE MENU · ofertas por vertical (ebook · curso · The Debt Program) */}
      <VerticalOffers testid="hub-pricing" />

      {/* 12b · ESTO ES TODO LO QUE RECIBES · product stack */}
      <OfferStack testid="hub-offer-stack" />

      {/* Price-lock warning · right below pricing */}
      <PriceIncreaseBanner
        currentPrice="$49/mes · fundador"
        nextPrice="$79/mes"
        date={closeLabel}
        testid="hub-price-warn"
      />

      {/* Mid-CTA · después del menú */}
      <MidScrollCTA
        kicker="— ÚNETE A LOS OTROS 8 DEL GRUPO 03"
        headline={<>Quedan cuatro lugares. <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>¿El tuyo?</span></>}
        subline={`Las inscripciones del Grupo 03 cierran el ${closeLabel}. Después, el siguiente abre hasta Q3 — meses pagando de más entre hoy y entonces.`}
        ctaText="Ver precios e inscribirme"
        ctaHref="#pricing"
        secondaryText="O descarga un ebook gratis →"
        secondaryHref="#biblioteca"
        variant="gold"
        testid="hub-mid-cta-2"
      />

      {/* 13b · BONUS STACK · $2,685 in bonuses included */}
      <BonusStack testid="hub-bonus-stack" />

      {/* 13c · CERTIFICATE MOCKUP · shareable credential */}
      <CertificateMockup testid="hub-certificate" />

      {/* 13c2 · GARANTÍA · todo por ganar, nada que perder */}
      <GuaranteeSection testid="hub-guarantee-section" />

      {/* Mid-CTA · decisión final (bottom-of-funnel) */}
      <MidScrollCTA
        kicker="— ¿YA ELEGISTE TU RUTA?"
        headline={<>Elige tu programa y <span style={{ fontStyle: "normal", color: GOLD, fontVariationSettings: '"SOFT" 100, "opsz" 144' }}>empieza hoy.</span></>}
        subline="Ebook, curso o cohorte — acceso inmediato tras el pago. Garantía de 14 días en todo."
        ctaText="Ver programas y precios"
        ctaHref="#pricing"
        secondaryText="Descarga un ebook gratis →"
        secondaryHref="#biblioteca"
        testid="hub-mid-cta-3"
      />

      {/* 13d · PAYMENT METHODS · LATAM-friendly */}
      <PaymentMethods testid="hub-payments" />

      {/* 15 · FAQ */}
      <FAQSection testid="hub-faq" />

      {/* Footer */}
      <footer
        className="hub-footer"
        style={{ borderTop: "1px solid rgba(15,56,44,0.06)", padding: "40px 40px", maxWidth: 1400, margin: "0 auto", display: "flex", justifyContent: "space-between", alignItems: "center", gap: 20, flexWrap: "wrap", position: "relative", zIndex: 5 }}
        data-testid="hub-footer"
      >
        <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
          <LogoMark size={46} />
          <p style={{ margin: 0, fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.2em", textTransform: "uppercase", color: CREAM, opacity: 0.5 }}>
            the debt guy · © 2026 · ernesto gómez berjón
          </p>
        </div>
        <div style={{ display: "flex", gap: 22, flexWrap: "wrap", alignItems: "center" }}>
          <Link to="/privacidad" data-testid="hub-footer-privacy-link" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: CREAM, opacity: 0.55, textDecoration: "none" }}>Privacidad</Link>
          <Link to="/terminos" data-testid="hub-footer-terms-link" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 10, letterSpacing: "0.18em", textTransform: "uppercase", color: CREAM, opacity: 0.55, textDecoration: "none" }}>Términos</Link>
          <Link to="/admin" style={{ fontFamily: "'Archivo', sans-serif", fontSize: 9, letterSpacing: "0.24em", textTransform: "uppercase", color: CREAM, opacity: 0.28, textDecoration: "none" }}>·</Link>
        </div>
      </footer>

      {/* Sticky CTA — appears after first viewport scroll */}
      <StickyCTABar
        title="¿List@ para levantar deuda como insider?"
        subtitle="El próximo grupo cierra pronto · inscripciones abiertas"
        ctaText="Ver precios"
        ctaHref="#pricing"
        secondaryText="Descarga un ebook gratis →"
        secondaryHref="#biblioteca"
        testid="hub-sticky-cta"
      />

      {/* Live social proof · bottom-left toast */}
      <RecentActivity testid="hub-recent-activity" />
    </div>
  );
}
